import { Box, CardContent, Collapse, Grid } from "@mui/material";
import CardHeader from "@mui/material/CardHeader";
import { grey } from "@mui/material/colors";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { ReactNode, forwardRef, useContext, useEffect, useState } from "react";
import { BentoBrand, CampaignBrand, CampaignStatus } from "schemas/dashboard";

import BrandBody from "features/Influencer/Discover/DetailedBrandView/components/BrandBody";
import { useBrandDetails } from "features/Influencer/Discover/DetailedBrandView/components/BrandBody/useBrandDetails";
import { BrandCardSource } from "features/Influencer/Discover/Results/schema";
import BrandTitle from "features/Influencer/Discover/Shared/BrandTitle";
import { trackEvent } from "utils/tracking";

import { CampaignBrandStatusChip } from "./CampaignBrandStatus";
import styles from "./styles";

interface BrandCardProps {
  status?: CampaignStatus;
  brand: BentoBrand;
  campaignBrand?: CampaignBrand;
  actions?: ReactNode;
}

export default forwardRef<HTMLDivElement, BrandCardProps>(
  ({ brand, campaignBrand, actions, status }, ref) => {
    const { setBrandNavigationHistory } = useContext(DiscoverViewContext);
    const [expanded, setExpanded] = useState(false);

    const handleClickCard = () => {
      trackEvent("Brand Card Clicked", {
        "Bento Brand ID": brand?.id,
        source: BrandCardSource.CAMPAIGN_BRAND,
      });
      setBrandNavigationHistory([brand]);
      setExpanded(!expanded);
    };

    const {
      fetchSelectedBentobrand,
      selectedBrand,
      fetchLoading,
      similarBrands,
      fetchSimilarLoading,
    } = useBrandDetails({ brandDetailsId: brand?.id });

    useEffect(() => {
      if (brand?.id && expanded) {
        fetchSelectedBentobrand(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [brand?.id, expanded]);

    return (
      <Box ref={ref} sx={[styles.brandCard]} key={brand?.id} component="div">
        <CardHeader
          sx={[
            {
              ".MuiCardHeader-avatar": {
                alignSelf: "flex-start",
                mt: 1.5,
              },
              cursor: "pointer",
              "@media (hover: hover) and (pointer: fine)": {
                "&:hover": { backgroundColor: grey[50] },
              },
            },
          ]}
          onClick={handleClickCard}
          title={
            <Grid
              container
              alignItems="center"
              gap={1}
              justifyContent="space-between"
            >
              <BrandTitle
                bentoBrand={brand}
                expanded={expanded}
                actions={actions}
                source={BrandCardSource.CAMPAIGN_BRAND}
              />
              {status !== CampaignStatus.pending_approval &&
                status !== CampaignStatus.expired &&
                campaignBrand?.status && (
                  <CampaignBrandStatusChip status={campaignBrand.status} />
                )}
            </Grid>
          }
        />
        {expanded && (
          <Collapse in={expanded}>
            <CardContent>
              <BrandBody
                selectedBrand={selectedBrand}
                fetchLoading={fetchLoading}
                similarBrands={similarBrands}
                fetchSimilarLoading={fetchSimilarLoading}
                source={BrandCardSource.CAMPAIGN_BRAND}
              />
            </CardContent>
          </Collapse>
        )}
      </Box>
    );
  },
);
